import { PurchaseParamsStruct } from "@lib/typechain/ProductsModule"
import { BigNumber } from "ethers"
import { ProductCart } from "@src/lib/cart/cartProductTypes"
import { quoteParams } from "@lib/useEthUsd"
import { readContract } from "@wagmi/core"
import { constants } from "@src/constants"
import PriceFeed from "@src/lib/artifacts/contracts/PriceFeed.sol/PriceFeed"
import ProductsModule from "@src/lib/artifacts/contracts/ProductsModule.sol/ProductsModule"
import { prepareWriteContract, writeContract } from "@wagmi/core"
import { zeroAddress } from "viem"

export type NewTransaction = {
  description: string
  hash: string
  confirmations?: number
}

export const payProductsConfig = async (
  buyer: string,
  productData: ProductCart[],
  chainId: number
) => {
  const quote = await readContract({
    address: constants[chainId].addresses["PriceFeed"],
    abi: PriceFeed.abi,
    functionName: "getQuote",
    args: quoteParams(chainId)
  })

  const ethUsd = quote as BigInt // Chainlink: Number(quote[1])
  let ethAmount: BigNumber
  let purchaseParams: PurchaseParamsStruct[] = []

  try {
    productData.forEach((product) => {
      const {
        slicerId,
        productId,
        quantity,
        price,
        currency,
        isUSD,
        extCallValue,
        buyerCustomData
      } = product

      const currentPrice = ethAmount || 0

      purchaseParams.push({
        buyer,
        slicerId,
        quantity,
        currency,
        productId,
        buyerCustomData
      })

      if (currency === zeroAddress) {
        const productPrice = isUSD
          ? BigNumber.from(price)
              .mul(BigNumber.from(10).pow(18)) // chainlink -> pow(24)
              .mul(102) // 2% overpayment to compensate for price fluctuations (repaid to buyer during tx)
              .div(100)
              .div(BigNumber.from(ethUsd))
              .add(extCallValue)
          : BigNumber.from(price).add(extCallValue)
        ethAmount = BigNumber.from(currentPrice).add(productPrice)
      }
    })

    return {
      address: constants[chainId].addresses["ProductsModule"],
      abi: ProductsModule.abi,
      functionName: "payProducts",
      args: [purchaseParams],
      value: ethAmount
    }
  } catch (err) {
    console.log(err)

    throw err
  }
}

export const payProducts = async (
  buyer: string,
  productData: ProductCart[],
  addRecentTransaction: (transaction: NewTransaction) => void | undefined,
  chainId: number
) => {
  try {
    const config = await payProductsConfig(buyer, productData, chainId)

    const { request } = await prepareWriteContract(config)

    const { hash } = await writeContract(request)

    addRecentTransaction?.({
      hash,
      description: "Checkout"
    })

    return hash
  } catch (err) {
    console.log(err)

    throw err
  }
}

export default payProducts
